// ===========================================
// Google Calendar React Query Hooks
// ===========================================
// Custom hooks for Google Calendar API operations
// OAuth, calendar selection, sync control

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  googleCalendarApi,
  SelectCalendarInput,
  UpdateSettingsInput,
  TriggerSyncInput,
} from '../api/googleCalendar.api';

// ===========================================
// QUERY KEYS
// ===========================================

export const googleCalendarKeys = {
  all: ['googleCalendar'] as const,
  status: () => [...googleCalendarKeys.all, 'status'] as const,
  calendars: () => [...googleCalendarKeys.all, 'calendars'] as const,
  syncErrors: () => [...googleCalendarKeys.all, 'syncErrors'] as const,
  studentMappings: () => [...googleCalendarKeys.all, 'studentMappings'] as const,
};

// ===========================================
// AUTH QUERIES
// ===========================================

/**
 * Check if Google Calendar is connected for the user
 */
export function useGoogleCalendarStatus() {
  return useQuery({
    queryKey: googleCalendarKeys.status(),
    queryFn: () => googleCalendarApi.getStatus(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Get OAuth authorization URL
 */
export function useGoogleCalendarAuthUrl() {
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: () => googleCalendarApi.getAuthUrl(),
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to get authorization URL', {
        variant: 'error',
      });
    },
  });
}

/**
 * Disconnect Google Calendar integration
 */
export function useDisconnectGoogleCalendar() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (removeEvents: boolean = true) => googleCalendarApi.disconnect(removeEvents),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: googleCalendarKeys.all });
      enqueueSnackbar('Google Calendar disconnected successfully', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to disconnect Google Calendar', {
        variant: 'error',
      });
    },
  });
}

// ===========================================
// CALENDAR QUERIES
// ===========================================

/**
 * List available calendars from user's Google account
 */
export function useGoogleCalendars() {
  return useQuery({
    queryKey: googleCalendarKeys.calendars(),
    queryFn: () => googleCalendarApi.listCalendars(),
    staleTime: 2 * 60 * 1000, // 2 minutes
    enabled: false, // Only fetch when explicitly called
  });
}

/**
 * Select which calendar to sync events to
 */
export function useSelectCalendar() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (input: SelectCalendarInput) => googleCalendarApi.selectCalendar(input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: googleCalendarKeys.status() });
      enqueueSnackbar(`Calendar "${data.calendarName}" selected successfully`, {
        variant: 'success',
      });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to select calendar', {
        variant: 'error',
      });
    },
  });
}

/**
 * Update calendar integration settings
 */
export function useUpdateCalendarSettings() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (input: UpdateSettingsInput) => googleCalendarApi.updateSettings(input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: googleCalendarKeys.status() });
      if (data.syncEnabled !== undefined) {
        enqueueSnackbar(
          data.syncEnabled ? 'Calendar sync enabled' : 'Calendar sync disabled',
          { variant: 'success' }
        );
      }
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to update settings', {
        variant: 'error',
      });
    },
  });
}

// ===========================================
// SYNC QUERIES
// ===========================================

/**
 * Get lessons with sync errors
 */
export function useGoogleCalendarSyncErrors() {
  return useQuery({
    queryKey: googleCalendarKeys.syncErrors(),
    queryFn: () => googleCalendarApi.getSyncErrors(),
    staleTime: 1 * 60 * 1000, // 1 minute
  });
}

/**
 * Trigger manual sync of lessons to calendar
 */
export function useTriggerCalendarSync() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (input?: TriggerSyncInput) => googleCalendarApi.triggerSync(input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: googleCalendarKeys.status() });
      queryClient.invalidateQueries({ queryKey: googleCalendarKeys.syncErrors() });
      enqueueSnackbar(data.message, {
        variant: data.errors > 0 ? 'warning' : 'success',
      });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to sync calendar', {
        variant: 'error',
      });
    },
  });
}

// ===========================================
// PER-STUDENT CALENDAR MAPPING (Parents)
// ===========================================

/**
 * List the caller's children + their per-child calendar mapping.
 * Only enabled when Google Calendar is connected (no point asking otherwise).
 * Returns [] for non-parents / single-child families — the UI gates on length.
 */
export function useStudentCalendarMappings(enabled: boolean = true) {
  return useQuery({
    queryKey: googleCalendarKeys.studentMappings(),
    queryFn: () => googleCalendarApi.getStudentMappings(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled,
  });
}

/**
 * Set / change a child's calendar. Invalidates the mappings list AND status (sync stats
 * change after the event move).
 */
export function useSetStudentMapping() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ studentId, input }: { studentId: string; input: SelectCalendarInput }) =>
      googleCalendarApi.setStudentMapping(studentId, input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: googleCalendarKeys.studentMappings() });
      queryClient.invalidateQueries({ queryKey: googleCalendarKeys.status() });
      const name = data.mapping?.calendarName;
      enqueueSnackbar(
        name ? `Calendar "${name}" set for this child` : 'Calendar updated for this child',
        { variant: 'success' }
      );
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to set calendar for this child', {
        variant: 'error',
      });
    },
  });
}

/**
 * Clear a child's mapping (revert to the default calendar). Invalidates mappings + status.
 */
export function useClearStudentMapping() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (studentId: string) => googleCalendarApi.clearStudentMapping(studentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: googleCalendarKeys.studentMappings() });
      queryClient.invalidateQueries({ queryKey: googleCalendarKeys.status() });
      enqueueSnackbar('Reverted to the default calendar for this child', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to update calendar for this child', {
        variant: 'error',
      });
    },
  });
}
